// Move money between wallet and the Savings Jar.
// 'save' txn convention: amount < 0 = deposit (cash leaves wallet, balance grows),
//                        amount > 0 = withdrawal (cash returns to wallet, balance shrinks).
// Category is always 'capital' — it's a balance-sheet move, not income/expense.

import type { GameState } from '../state/types.js';
import { applyTxn } from '../ledger/append.js';

export type SavingsError = {
  code: 'INSUFFICIENT_CASH' | 'INSUFFICIENT_SAVINGS' | 'NON_POSITIVE_AMOUNT';
  message: string;
};

export function deposit(
  state: GameState,
  amount: number,
): { ok: true; value: GameState } | { ok: false; error: SavingsError } {
  if (amount <= 0) {
    return { ok: false, error: { code: 'NON_POSITIVE_AMOUNT', message: 'Deposit must be > 0.' } };
  }
  if (state.wallet.cash < amount) {
    return { ok: false, error: { code: 'INSUFFICIENT_CASH', message: 'Not enough cash.' } };
  }
  const next = applyTxn(state, {
    type: 'save',
    amount: -amount,
    category: 'capital',
    note: 'Deposit to Savings Jar',
  });
  return {
    ok: true,
    value: {
      ...next,
      savings: { ...next.savings, balance: next.savings.balance + amount },
    },
  };
}

export function withdraw(
  state: GameState,
  amount: number,
): { ok: true; value: GameState } | { ok: false; error: SavingsError } {
  if (amount <= 0) {
    return { ok: false, error: { code: 'NON_POSITIVE_AMOUNT', message: 'Withdrawal must be > 0.' } };
  }
  if (state.savings.balance < amount) {
    return { ok: false, error: { code: 'INSUFFICIENT_SAVINGS', message: 'Not enough in savings.' } };
  }
  const next = applyTxn(state, {
    type: 'save',
    amount: +amount,
    category: 'capital',
    note: 'Withdraw from Savings Jar',
  });
  return {
    ok: true,
    value: {
      ...next,
      savings: { ...next.savings, balance: next.savings.balance - amount },
    },
  };
}
